// ABC SEO/GEO — Local Listings (citations management).
// One "source of truth" for the business NAP, an auto-checked Google Business
// Profile (we hold the OAuth connection), and a guided, link-driven checklist
// for the directories we can't submit to automatically — focused on Canada.
(function () {
  const { Icon } = window.SGIcons;
  const { Card, CardHead, PageHead } = window.SGKit;

  const apiFetch = window.SGLive.apiFetch;

  // ── persistence (local, per-browser — a real per-client store comes with
  //    the GHL-brokered backend) ──
  const NAP_KEY = 'abc_listings_nap';
  const DONE_KEY = 'abc_listings_done';
  const loadJSON = (k, fallback) => {
    try { return JSON.parse(localStorage.getItem(k)) || fallback; } catch { return fallback; }
  };
  // Stored NAP for a domain, minus anything left behind by the old hard-coded
  // default (see EMPTY_NAP below).
  const loadNap = (d) => {
    const stored = loadJSON(NAP_KEY + (d ? ':' + d : ''), null);
    if (isLegacyNap(stored, d)) { try { localStorage.removeItem(NAP_KEY + (d ? ':' + d : '')); } catch { /* ignore */ } return {}; }
    return stored || {};
  };
  // Per-domain storage keys so each tracked domain keeps its OWN listing profile.
  const napKeyFor = (d) => NAP_KEY + (d ? ':' + d : '');
  const doneKeyFor = (d) => DONE_KEY + (d ? ':' + d : '');

  // This used to be hard-coded to AI Biz Connect's OWN name, address, phone and
  // category. Every client, on every domain, therefore opened this screen with
  // our details presented as their "source of truth" -- and the Google Business
  // Profile lookup then searched for AI Biz Connect instead of the client. An
  // ABRE domain was looking up the agency, not Ali Bolourchi.
  //
  // Nothing is invented now. The business identity is read from the per-domain
  // profile the platform already derives from the site itself; anything we do
  // not know is left blank for a person to fill, because a blank field asks a
  // question while a wrong one quietly answers it.
  const EMPTY_NAP = { name: '', address: '', phone: '', website: '', category: '' };

  // Name of the old hard-coded default. Any stored NAP still carrying it on a
  // domain that is not ours is our data sitting on someone else's business, so
  // it is discarded rather than carried forward.
  const LEGACY_DEFAULT_NAME = 'AI Biz Connect';
  const isLegacyNap = (stored, d) =>
    !!stored && stored.name === LEGACY_DEFAULT_NAME && !/aibizconnect/i.test(d || '');

  // Website prefills to the domain in view; the rest comes from the profile.
  const defaultNapFor = (d) => ({ ...EMPTY_NAP, ...(d ? { website: d } : {}) });

  // Per-domain profile -> NAP fields. brand/city/trade are derived by the site
  // profiler from the site's own JSON-LD, meta and copy.
  const napFromProfile = (prof, d) => ({
    ...defaultNapFor(d),
    ...(prof && prof.brand ? { name: prof.brand } : {}),
    ...(prof && prof.target_location ? { address: prof.target_location } : {}),
    ...(prof && prof.business_type ? { category: prof.business_type } : {}),
  });

  // Directories. group: 'auto' = we hold an API connection; 'guided' = manual,
  // with step-by-step help. importance drives the "action needed" tally.
  const DIRS = [
    // url = where to go when there is NO profile yet. manageUrl = where to go when
    // there is one. They were the same link, and it was the create flow: a client
    // who already had a profile was being walked into making a SECOND one, which
    // splits reviews and is worse than the problem it was meant to solve.
    { id: 'gbp', name: 'Google Business Profile', cat: 'Maps & Search', importance: 'Critical', group: 'auto', method: 'api',
      url: 'https://business.google.com/create', manageUrl: 'https://business.google.com/dashboard', initial: 'G', color: '#4285F4',
      why: 'The single biggest local ranking factor — it powers Google Maps, the local pack, and increasingly AI Overviews.',
      steps: [
        'Already connected through your Google account — no submission needed.',
        'Confirm the name, category, hours, phone and website match your source of truth above.',
        'Add at least 3 photos and turn on messaging.',
        'Ask recent customers for reviews — they feed both Google and AI engines.',
      ],
      verify: 'Google now defaults to video verification (record your signage/premises) — or instant if your website is verified in Search Console. Confirm: search your business name plus your city on Google & Maps. (We can auto-manage this via the Business Profile API once your profile is verified 60+ days and the API is approved.)' },
    { id: 'bing', name: 'Bing Places for Business', cat: 'Maps & Search', importance: 'High', group: 'guided', method: 'import',
      url: 'https://www.bing.com/forbusiness', initial: 'b', color: '#0A6ED1',
      why: 'Powers Bing Maps and Microsoft Copilot answers. You can import everything from Google in one step — almost no manual entry.',
      steps: [
        'Go to bing.com/forbusiness and sign in with the business Microsoft account.',
        'Choose “Import from Google Business Profile” → authorize Google → pick this listing.',
        'Review the imported details and submit.',
      ],
      verify: 'Instant verification when imported from an already-verified Google profile (otherwise a phone/email code). Confirm: search Bing.com and Bing Maps.' },
    { id: 'apple', name: 'Apple Business Connect', cat: 'Maps', importance: 'High', group: 'guided', method: 'manual',
      url: 'https://business.apple.com/', initial: '', color: '#1d1d1f', dark: true,
      why: 'Controls how you appear in Apple Maps, Siri and Spotlight on every iPhone.',
      steps: [
        'Go to business.apple.com and create a free Apple Account for the business.',
        'Complete the company profile, then Add a location (claim the Apple Maps place or add new).',
        'Set category, hours and a logo, then submit for review.',
      ],
      verify: 'Apple verifies by document upload (utility bill / lease / insurance) OR a phone call — up to 5 business days. Confirm: search the business in Apple Maps; the card becomes editable.' },
    { id: 'facebook', name: 'Facebook / Meta Page', cat: 'Social', importance: 'High', group: 'guided', method: 'manual',
      url: 'https://www.facebook.com/pages/create', initial: 'f', color: '#1877F2',
      why: 'A complete Page with a matching NAP is a trust signal both Google and AI engines read.',
      steps: [
        'Create the Page at facebook.com/pages/create (Business/Brand → category “Marketing Agency”).',
        'Fill the About → Contact section with the exact name, phone, address and website above.',
        'Add your service area, hours and images, then publish.',
      ],
      verify: 'No verification needed to publish. Confirm: your Page is public immediately. (Page info can later be managed via the Meta Graph API.)' },
    { id: 'yelp', name: 'Yelp for Business', cat: 'Reviews', importance: 'High', group: 'guided', method: 'manual',
      url: 'https://biz.yelp.com/claim', initial: 'Y', color: '#D32323',
      why: 'A major review source that Apple Maps and several AI assistants pull from.',
      steps: [
        'Go to biz.yelp.com/claim and search for your business.',
        'Claim it, or click “Add to Yelp for free” if it’s missing.',
        'Complete your hours, categories and photos.',
      ],
      verify: 'Verify with an emailed code or a phone call/text. Confirm: an owner/verified badge appears on your Yelp page. (Yelp has no listings write API — claim by hand.)' },
    { id: 'yp', name: 'YellowPages.ca', cat: 'Directory', importance: 'High', group: 'guided', method: 'manual',
      url: 'https://solutions.yp.ca/free-online-listing', initial: 'YP', color: '#1B6E3C',
      why: 'Canada’s largest online directory — a core citation source for Canadian local SEO. Do this before Canada411 (shared data backbone).',
      steps: [
        'Open solutions.yp.ca/free-online-listing.',
        'Create a free basic listing with your exact NAP.',
        'Submit for review.',
      ],
      verify: 'Email confirmation + manual review. Confirm: search yellowpages.ca for your name.' },
    { id: '411', name: '411.ca', cat: 'Directory', importance: 'Medium', group: 'guided', method: 'manual',
      url: 'https://411.ca/add-your-business', initial: '4', color: '#0F7A3D',
      why: 'A widely-referenced Canadian business directory.',
      steps: ['Open 411.ca/add-your-business.', 'Submit your exact NAP.'],
      verify: 'Manual approval (~2 business days). Confirm: search 411.ca.' },
    { id: 'canada411', name: 'Canada411', cat: 'Directory', importance: 'Medium', group: 'guided', method: 'manual',
      url: 'https://www.canada411.ca/', initial: 'C', color: '#005DAA',
      why: 'A long-standing national directory that feeds many downstream sites.',
      steps: ['Go to canada411.ca → “Add your business” (uses your Yellow Pages account).', 'Submit your listing.'],
      verify: 'Near-instant via your YP account. Confirm: search canada411.ca.' },
    { id: 'bbb', name: 'Better Business Bureau', cat: 'Trust', importance: 'Medium', group: 'guided', method: 'manual',
      url: 'https://www.bbb.org/get-listed', initial: 'BBB', color: '#0061A0',
      why: 'A strong trust / E-E-A-T signal; a free profile is available even without paid accreditation.',
      steps: [
        'Open bbb.org/get-listed and search for your business (a profile may already exist).',
        'Claim it or add a free business profile.',
        'Keep the NAP identical to your source of truth.',
      ],
      verify: 'A BBB rep phones you back to confirm — keep a reachable number handy. Confirm: search bbb.org. (Accreditation is separate and paid.)' },
    { id: 'foursquare', name: 'Foursquare', cat: 'Maps data', importance: 'Medium', group: 'guided', method: 'aggregator',
      url: 'https://foursquare.com/venue/claim', initial: '4sq', color: '#F94877',
      why: 'The largest location-data aggregator — one free self-add propagates to Apple Maps, Uber, Snapchat, Yahoo and more.',
      steps: ['Open foursquare.com/venue/claim and search Richmond Hill.', 'Claim your venue, or add it if missing.', 'Create the owner account and complete NAP, hours and category.'],
      verify: 'Account-based claim. Confirm: a claimed/verified badge on the venue; downstream apps update over 4–12 weeks.' },
    { id: 'cylex', name: 'Cylex Canada', cat: 'Directory', importance: 'Low', group: 'guided', method: 'bulk',
      url: 'https://www.cylex-canada.ca/', initial: 'Cy', color: '#1A7FC1',
      why: 'An extra Canadian citation — and it accepts a bulk/CSV feed for multiple locations.',
      steps: ['Visit cylex-canada.ca → “Add company” (or use Bulk Upload for many locations).', 'Submit your NAP and confirm by email.'],
      verify: 'Email registration. Confirm: search cylex-canada.ca.' },
    { id: 'hotfrog', name: 'Hotfrog', cat: 'Directory', importance: 'Low', group: 'guided', method: 'manual',
      url: 'https://www.hotfrog.ca/', initial: 'Hf', color: '#E5552E',
      why: 'A free Canadian business directory (owned by Locafy).',
      steps: ['Go to hotfrog.ca → add your business.', 'Enter your exact NAP and category.'],
      verify: 'Email / account confirmation. Confirm: search hotfrog.ca.' },
    { id: 'n49', name: 'n49', cat: 'Directory', importance: 'Low', group: 'guided', method: 'manual',
      url: 'https://www.n49.com/add-business/', initial: 'n49', color: '#0E9F6E',
      why: 'A free Canadian local directory and review site.',
      steps: ['Open n49.com/add-business.', 'Submit your NAP.'],
      verify: 'Click the email confirmation link n49 sends. Confirm: search n49.com.' },
  ];

  // automation method → badge styling + label
  const METHOD_STYLE = {
    api: { label: 'AUTO · API', c: 'var(--sg-green)', bg: 'var(--sg-green-50)' },
    import: { label: 'AUTO · IMPORT', c: 'var(--sg-green)', bg: 'var(--sg-green-50)' },
    aggregator: { label: 'AGGREGATOR', c: 'var(--sg-violet-700)', bg: 'var(--sg-violet-100)' },
    bulk: { label: 'BULK FEED', c: 'var(--sg-blue-600)', bg: 'var(--sg-blue-50)' },
    manual: { label: 'MANUAL', c: 'var(--sg-text-3)', bg: 'var(--sg-sunken)' },
  };

  const IMP_STYLE = {
    Critical: { c: 'var(--sg-red)', bg: 'var(--sg-red-50)' },
    High: { c: 'var(--sg-blue-600)', bg: 'var(--sg-blue-50)' },
    Medium: { c: 'var(--sg-amber)', bg: 'var(--sg-amber-50)' },
    Low: { c: 'var(--sg-text-3)', bg: 'var(--sg-sunken)' },
  };

  // A hard-coded SCAN object used to live here: one real scan of AI Biz Connect's
  // OWN listings from 2026-06-22, frozen into the source. It was already dead
  // code -- the screen computes realFixes/realScore from this client's actual
  // Google match and NAP completeness -- but it named our address and told the
  // reader to "update to AI Biz Connect", which is exactly the wrong advice for
  // every client who is not us. Removed so it cannot be revived by accident.

  function PriorityFixes({ fixes, done, onToggleDone }) {
    if (!fixes || fixes.length === 0) {
      return (
        <Card>
          <CardHead title="Priority fixes" sub="The most important local-SEO gaps for your business, worst first" />
          <div style={{ padding: '22px 20px', fontSize: 13, color: 'var(--sg-text-2)' }}>No urgent listing issues — your Google profile is verified and your details are complete. 🎉</div>
        </Card>
      );
    }
    return (
      <Card>
        <CardHead title="Priority fixes" sub="The most important local-SEO gaps for your business, worst first"
          right={<span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--sg-amber)', background: 'var(--sg-amber-50)', padding: '3px 10px', borderRadius: 'var(--sg-radius-pill)' }}>{fixes.length} task{fixes.length === 1 ? '' : 's'}</span>} />
        {fixes.map((f, i) => {
          const imp = IMP_STYLE[f.sev];
          const isDone = !!done[f.id];
          return (
            <div key={f.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 14, padding: '15px 20px', borderBottom: i < fixes.length - 1 ? '1px solid var(--sg-border)' : 'none' }}>
              <button onClick={() => onToggleDone(f.id)} aria-label="Toggle done"
                style={{ width: 22, height: 22, marginTop: 1, flex: 'none', borderRadius: 6, cursor: 'pointer',
                  border: '1.5px solid ' + (isDone ? 'var(--sg-green)' : 'var(--sg-border-strong, #C7D0DD)'),
                  background: isDone ? 'var(--sg-green)' : 'var(--sg-card)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                {isDone && <Icon name="check" size={14} color="#fff" />}
              </button>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--sg-text)', textDecoration: isDone ? 'line-through' : 'none', opacity: isDone ? 0.55 : 1 }}>{f.task}</span>
                  <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', color: imp.c, background: imp.bg, padding: '2px 8px', borderRadius: 'var(--sg-radius-pill)' }}>{f.sev.toUpperCase()}</span>
                  {f.auto
                    ? <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', color: 'var(--sg-green)', background: 'var(--sg-green-50)', padding: '2px 8px', borderRadius: 'var(--sg-radius-pill)' }}>● AUTO-READY</span>
                    : <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', color: 'var(--sg-text-3)', background: 'var(--sg-sunken)', padding: '2px 8px', borderRadius: 'var(--sg-radius-pill)' }}>MANUAL</span>}
                </div>
                <div style={{ fontSize: 12.5, color: 'var(--sg-text-2)', marginTop: 4, lineHeight: 1.5 }}>{f.detail}</div>
              </div>
              {f.url && (
                <a href={f.url} target="_blank" rel="noopener noreferrer"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 34, padding: '0 13px', flex: 'none', borderRadius: 'var(--sg-radius-pill)', textDecoration: 'none',
                    border: '1px solid var(--sg-border)', background: 'var(--sg-card)', fontFamily: 'var(--sg-font-sans)', fontSize: 12.5, fontWeight: 600, color: 'var(--sg-text-2)' }}>
                  Open <Icon name="external-link" size={13} />
                </a>
              )}
            </div>
          );
        })}
      </Card>
    );
  }

  function NapField({ label, value, onChange, mono }) {
    return (
      <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--sg-text-3)' }}>{label}</span>
        <input value={value} onChange={(e) => onChange(e.target.value)}
          style={{ height: 40, padding: '0 12px', border: '1px solid var(--sg-border)', borderRadius: 'var(--sg-radius-md)',
            background: 'var(--sg-sunken)', fontFamily: mono ? 'var(--sg-font-sans)' : 'var(--sg-font-sans)', fontSize: 13.5,
            color: 'var(--sg-text)', outline: 'none' }} />
      </label>
    );
  }

  function Logo({ d }) {
    return (
      <span style={{ width: 38, height: 38, flex: 'none', borderRadius: 10, background: d.color, color: '#fff',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800,
        fontSize: d.initial.length > 1 ? 11 : 16, fontFamily: 'var(--sg-font-display)', letterSpacing: '-0.01em' }}>
        {d.initial || <Icon name="map-pin" size={18} color="#fff" />}
      </span>
    );
  }

  function DirRow({ d, listed, expanded, onToggleExpand, onToggleDone, auto, actionUrl, actionLabel, accountNote }) {
    const imp = IMP_STYLE[d.importance];
    const meth = METHOD_STYLE[d.method];
    const status = listed
      ? { label: auto ? 'Connected' : 'Listed', c: 'var(--sg-green)', bg: 'var(--sg-green-50)', dot: 'var(--sg-green)' }
      : (d.importance === 'Critical' || d.importance === 'High')
        ? { label: 'Action needed', c: 'var(--sg-amber)', bg: 'var(--sg-amber-50)', dot: 'var(--sg-amber)' }
        : { label: 'Not yet listed', c: 'var(--sg-text-3)', bg: 'var(--sg-sunken)', dot: 'var(--sg-text-3)' };
    return (
      <div style={{ borderBottom: '1px solid var(--sg-border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 20px' }}>
          <Logo d={d} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 14.5, fontWeight: 600, color: 'var(--sg-text)' }}>{d.name}</span>
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', color: imp.c, background: imp.bg, padding: '2px 8px', borderRadius: 'var(--sg-radius-pill)' }}>{d.importance.toUpperCase()}</span>
              {meth && <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', color: meth.c, background: meth.bg, padding: '2px 8px', borderRadius: 'var(--sg-radius-pill)' }}>{meth.label}</span>}
            </div>
            <div style={{ fontSize: 12, color: 'var(--sg-text-3)', marginTop: 2 }}>{d.cat}</div>
          </div>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: status.c, background: status.bg, padding: '5px 11px', borderRadius: 'var(--sg-radius-pill)' }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: status.dot }} /> {status.label}
          </span>
          <button onClick={onToggleExpand} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, height: 34, padding: '0 13px', border: '1px solid var(--sg-border)', borderRadius: 'var(--sg-radius-pill)', background: 'var(--sg-card)', cursor: 'pointer', fontFamily: 'var(--sg-font-sans)', fontSize: 12.5, fontWeight: 600, color: 'var(--sg-text-2)' }}>
            How to <Icon name={expanded ? 'chevron-up' : 'chevron-down'} size={14} />
          </button>
        </div>
        {expanded && (
          <div style={{ padding: '4px 20px 20px 72px', animation: 'sg-fade 0.25s var(--sg-ease)' }}>
            <div style={{ background: 'var(--sg-sunken)', border: '1px solid var(--sg-border)', borderRadius: 'var(--sg-radius-lg)', padding: 16 }}>
              <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--sg-text-2)', lineHeight: 1.55 }}>
                <Icon name="info" size={14} color="var(--sg-blue-600)" style={{ verticalAlign: '-2px', marginRight: 6 }} />{d.why}
              </p>
              <ol style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
                {d.steps.map((s, i) => (
                  <li key={i} style={{ display: 'flex', gap: 10, fontSize: 13, color: 'var(--sg-text)' }}>
                    <span style={{ width: 20, height: 20, flex: 'none', borderRadius: '50%', background: 'var(--sg-blue-50)', color: 'var(--sg-blue-600)', fontSize: 11, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{i + 1}</span>
                    <span style={{ lineHeight: 1.5 }}>{s}</span>
                  </li>
                ))}
              </ol>
              {d.verify && (
                <div style={{ display: 'flex', gap: 9, marginTop: 14, padding: 12, borderRadius: 'var(--sg-radius-md)', background: 'var(--sg-card)', border: '1px solid var(--sg-border)' }}>
                  <Icon name="check" size={15} color="var(--sg-green)" style={{ marginTop: 1, flex: 'none' }} />
                  <div style={{ fontSize: 12.5, color: 'var(--sg-text-2)', lineHeight: 1.5 }}>
                    <span style={{ fontWeight: 700, color: 'var(--sg-text)' }}>Verify: </span>{d.verify}
                  </div>
                </div>
              )}
              {accountNote && (
                <div style={{ display: 'flex', gap: 9, alignItems: 'flex-start', marginTop: 14, padding: '10px 12px', borderRadius: 10, background: 'var(--sg-bg-2, #F8FAFC)' }}>
                  <Icon name="info" size={14} color="var(--sg-text-3)" />
                  <span style={{ fontSize: 12, color: 'var(--sg-text-2)', lineHeight: 1.55 }}>{accountNote}</span>
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 16 }}>
                <a href={actionUrl || d.url} target="_blank" rel="noopener noreferrer"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 7, height: 38, padding: '0 16px', borderRadius: 'var(--sg-radius-pill)', textDecoration: 'none',
                    fontFamily: 'var(--sg-font-sans)', fontWeight: 700, fontSize: 13, color: '#fff', background: 'var(--sg-grad-brand)', boxShadow: '0 3px 10px rgba(37,99,235,0.26)' }}>
                  {actionLabel || (auto ? 'Open dashboard' : 'Open & submit')} <Icon name="external-link" size={14} color="#fff" />
                </a>
                {!auto && (
                  <button onClick={onToggleDone}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 7, height: 38, padding: '0 15px', borderRadius: 'var(--sg-radius-pill)', cursor: 'pointer',
                      border: '1px solid ' + (listed ? 'var(--sg-green)' : 'var(--sg-border)'), background: listed ? 'var(--sg-green-50)' : 'var(--sg-card)',
                      fontFamily: 'var(--sg-font-sans)', fontWeight: 700, fontSize: 13, color: listed ? 'var(--sg-green)' : 'var(--sg-text-2)' }}>
                    <Icon name="check" size={15} /> {listed ? 'Marked as listed' : 'Mark as listed'}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Real Google Business Profile status — detected & matched to this business.
  // -- Review behaviour ----------------------------------------------
  // The star rating tells an owner what they already know. What they do NOT
  // know is how many reviews they never answered and how long the answered ones
  // waited -- and that is the fixable part.
  //
  // Starting a pull COSTS MONEY (the provider charges on task creation), so it
  // never runs on render. A person presses the button. Collecting afterwards is
  // free and repeatable, so the result is polled without further charge.
  function ReviewBehaviour({ listing, domain }) {
    const [state, setState] = React.useState('idle'); // idle | starting | pending | ready | error
    const [summary, setSummary] = React.useState(null);
    const [error, setError] = React.useState('');
    const timer = React.useRef(null);

    React.useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
    // A different business means a different pull -- never show one business's
    // reviews under another's name.
    React.useEffect(() => { setState('idle'); setSummary(null); setError(''); }, [domain, listing && listing.cid]);

    function collect(taskId, attempt) {
      window.SGLive.collectReviews(taskId).then((d) => {
        if (d && d.status === 'ready') { setSummary(d.summary); setState('ready'); return; }
        // The scrape takes minutes. Keep waiting rather than reporting zero
        // reviews for a business that has hundreds.
        if (attempt < 20) {
          timer.current = setTimeout(() => collect(taskId, attempt + 1), 15000);
        } else {
          setError('Still collecting. Come back shortly and press Check again -- the result is saved, so it is not charged twice.');
          setState('error');
        }
      });
    }

    function start() {
      setState('starting'); setError('');
      window.SGLive.startReviewPull({
        name: listing.title || '', domain: domain || '',
        phone: listing.phone || '', cid: listing.cid || '', placeId: listing.placeId || '',
      }).then((j) => {
        if (!j || j.success === false) { setError((j && j.error) || 'Could not start the review check.'); setState('error'); return; }
        setState('pending');
        collect(j.data.taskId, 0);
      });
    }

    if (state === 'idle') {
      return (
        <div style={{ padding: '14px 20px', borderTop: '1px solid var(--sg-border)', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 240, fontSize: 12.5, color: 'var(--sg-text-2)', lineHeight: 1.55 }}>
            <strong style={{ color: 'var(--sg-text)' }}>How well does this business answer its reviews?</strong>{' '}
            Reply rate and response time say more to a customer -- and to Google -- than the star rating does.
          </div>
          <button onClick={start} style={{ flex: 'none', display: 'inline-flex', alignItems: 'center', gap: 7, height: 36, padding: '0 15px', border: '1px solid var(--sg-border)', borderRadius: 'var(--sg-radius-pill)', background: 'var(--sg-card)', color: 'var(--sg-blue-600)', cursor: 'pointer', fontFamily: 'var(--sg-font-sans)', fontWeight: 700, fontSize: 12.5 }}>
            <Icon name="star" size={13} /> Check review behaviour
          </button>
        </div>
      );
    }
    if (state === 'starting' || state === 'pending') {
      return (
        <div style={{ padding: '14px 20px', borderTop: '1px solid var(--sg-border)', fontSize: 12.5, color: 'var(--sg-text-2)' }}>
          Reading this listing's reviews... this takes a few minutes. You can leave the page -- the result is kept.
        </div>
      );
    }
    if (state === 'error') {
      return (
        <div style={{ padding: '14px 20px', borderTop: '1px solid var(--sg-border)', display: 'flex', alignItems: 'center', gap: 10, fontSize: 12.5, color: 'var(--sg-text-2)' }}>
          <Icon name="alert-triangle" size={15} color="var(--sg-amber)" />
          <span style={{ flex: 1 }}>{error}</span>
          <button onClick={start} style={{ flex: 'none', background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'var(--sg-font-sans)', fontWeight: 700, fontSize: 12.5, color: 'var(--sg-blue-600)' }}>Check again</button>
        </div>
      );
    }

    const s = summary || {};
    const stat = (label, value, tone) => (
      <div style={{ textAlign: 'center', minWidth: 92 }}>
        <div className="sg-tnum" style={{ fontFamily: 'var(--sg-font-display)', fontWeight: 700, fontSize: 21, lineHeight: 1, color: tone || 'var(--sg-text)' }}>{value}</div>
        <div style={{ fontSize: 11, color: 'var(--sg-text-3)', marginTop: 4 }}>{label}</div>
      </div>
    );
    // null means "nothing to reply to yet", which is not the same as 0%.
    const rate = s.replyRatePct == null ? '--' : s.replyRatePct + '%';
    const rateTone = s.replyRatePct == null ? 'var(--sg-text-3)'
      : s.replyRatePct >= 80 ? 'var(--sg-green)' : s.replyRatePct >= 40 ? 'var(--sg-amber)' : 'var(--sg-red, #DC2626)';
    return (
      <div style={{ padding: '14px 20px', borderTop: '1px solid var(--sg-border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
          {stat('Replied to', rate, rateTone)}
          {stat('Typical reply time', s.medianReplyLabel || '--')}
          {stat('Unanswered 1-2 star', s.negativeUnanswered == null ? 0 : s.negativeUnanswered, s.negativeUnanswered ? 'var(--sg-red, #DC2626)' : 'var(--sg-text)')}
          {stat('Reviews read', s.analyzed == null ? 0 : s.analyzed)}
          <div style={{ flex: 1, minWidth: 200, fontSize: 12.5, color: 'var(--sg-text-2)', lineHeight: 1.55 }}>
            {s.negativeUnanswered
              ? s.negativeUnanswered + ' negative review' + (s.negativeUnanswered === 1 ? '' : 's') + ' with no reply. Those are the ones prospects read first -- answer them before anything else.'
              : s.replyRatePct != null && s.replyRatePct < 80
                ? 'Replying to every review, not just the good ones, is the cheapest trust signal available.'
                : 'Reviews are being answered well. Keep it up -- consistency is what customers notice.'}
          </div>
        </div>
        <div style={{ fontSize: 11.5, color: 'var(--sg-text-3)', marginTop: 10 }}>
          Based on the {s.analyzed == null ? 0 : s.analyzed} most recent reviews{s.totalOnGoogle ? ' of ' + s.totalOnGoogle + ' on Google' : ''}. Reply time is the
          typical (median) gap, so one very late answer does not distort it.
        </div>
      </div>
    );
  }

  function GoogleListingCard({ listing, domain }) {
    if (listing === undefined) {
      return <Card style={{ padding: 18, fontSize: 13, color: 'var(--sg-text-2)', display: 'flex', alignItems: 'center', gap: 10 }}><Icon name="refresh-cw" size={16} color="var(--sg-text-3)" /> Checking Google for your business listing…</Card>;
    }
    const found = listing && listing.found;
    if (found) {
      return (
        <Card style={{ padding: 0, overflow: 'hidden', border: '1px solid var(--sg-green-100, #CDEBD6)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '18px 20px', flexWrap: 'wrap' }}>
            <span style={{ width: 46, height: 46, flex: 'none', borderRadius: 12, background: 'var(--sg-green-50)', color: 'var(--sg-green)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="check" size={24} /></span>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontFamily: 'var(--sg-font-display)', fontWeight: 700, fontSize: 16, color: 'var(--sg-text)' }}>Listed on Google</span>
                <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', color: 'var(--sg-green)', background: 'var(--sg-green-50)', padding: '2px 8px', borderRadius: 'var(--sg-radius-pill)' }}>● LIVE · MATCHED</span>
                {listing.claimed && <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--sg-blue-600)', background: 'var(--sg-blue-50)', padding: '2px 8px', borderRadius: 'var(--sg-radius-pill)' }}>CLAIMED</span>}
              </div>
              <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--sg-text)', marginTop: 4 }}>{listing.title}</div>
              <div style={{ fontSize: 12.5, color: 'var(--sg-text-2)', marginTop: 2 }}>{listing.address}{listing.phone ? ' · ' + listing.phone : ''}</div>
            </div>
            {listing.rating != null && (
              <div style={{ textAlign: 'center', flex: 'none', padding: '0 8px' }}>
                <div className="sg-tnum" style={{ fontFamily: 'var(--sg-font-display)', fontWeight: 700, fontSize: 24, color: 'var(--sg-amber)', lineHeight: 1 }}>★ {listing.rating}</div>
                <div style={{ fontSize: 11.5, color: 'var(--sg-text-3)', marginTop: 4 }}>{listing.reviews || 0} reviews</div>
              </div>
            )}
            {listing.url && <a href={listing.url} target="_blank" rel="noopener noreferrer" style={{ flex: 'none', display: 'inline-flex', alignItems: 'center', gap: 6, height: 38, padding: '0 16px', borderRadius: 'var(--sg-radius-pill)', border: '1px solid var(--sg-border)', background: 'var(--sg-card)', color: 'var(--sg-blue-600)', textDecoration: 'none', fontFamily: 'var(--sg-font-sans)', fontWeight: 700, fontSize: 13 }}>View on Maps <Icon name="external-link" size={13} /></a>}
          </div>
          <div style={{ padding: '10px 20px', borderTop: '1px solid var(--sg-border)', fontSize: 12, color: 'var(--sg-text-3)' }}>Matched to your business by {listing.matchedOn === 'domain' ? 'website' : listing.matchedOn}. Make sure the name, address &amp; phone above match this listing exactly.</div>
          <ReviewBehaviour listing={listing} domain={domain} />
        </Card>
      );
    }
    return (
      <Card style={{ padding: 20, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', background: 'var(--sg-amber-50)', border: '1px solid var(--sg-amber-100, #F6E6C7)' }}>
        <span style={{ width: 46, height: 46, flex: 'none', borderRadius: 12, background: 'var(--sg-card)', color: 'var(--sg-amber)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="map-pin" size={24} /></span>
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ fontFamily: 'var(--sg-font-display)', fontWeight: 700, fontSize: 15.5, color: 'var(--sg-text)' }}>No Google listing found for your business</div>
          <div style={{ fontSize: 13, color: 'var(--sg-text-2)', marginTop: 4, lineHeight: 1.5 }}>We couldn’t find a Google Business Profile matching your website or phone.{listing && listing.topResult ? ` (The top result for your name is “${listing.topResult}” — a different business.)` : ''} A verified profile is the single biggest local-ranking win.</div>
        </div>
        <a href="https://business.google.com/create" target="_blank" rel="noopener noreferrer" style={{ flex: 'none', display: 'inline-flex', alignItems: 'center', gap: 7, height: 42, padding: '0 18px', borderRadius: 'var(--sg-radius-pill)', border: 'none', background: 'var(--sg-grad-brand)', color: '#fff', textDecoration: 'none', fontFamily: 'var(--sg-font-sans)', fontWeight: 700, fontSize: 13.5 }}>Create your profile <Icon name="external-link" size={14} /></a>
      </Card>
    );
  }

  const d0GbpManage = 'https://business.google.com/dashboard';

  function Listings() {
    const { site, prettySite } = window.SGLive.useLive();
    const domain = site ? prettySite(site) : '';
    const [nap, setNap] = React.useState(() => ({ ...defaultNapFor(domain), ...loadNap(domain) }));
    const [done, setDone] = React.useState(() => loadJSON(doneKeyFor(domain), {}));
    // Each domain has its own listing profile — reload it when the domain changes.
    React.useEffect(() => {
      setNap({ ...defaultNapFor(domain), ...loadNap(domain) });
      setDone(loadJSON(doneKeyFor(domain), {}));
    }, [domain]);
    // Fill the blanks from what the platform already knows about THIS domain.
    // Anything the user typed wins — a saved edit is a decision, and a derived
    // value must never overwrite it.
    React.useEffect(() => {
      let live = true;
      window.SGLive.fetchProfile().then((prof) => {
        if (!live || !prof) return;
        const stored = loadNap(domain);
        setNap({ ...napFromProfile(prof, domain), ...stored });
      });
      return () => { live = false; };
    }, [domain]);
    const [expanded, setExpanded] = React.useState(null);
    const [gbpConnected, setGbpConnected] = React.useState(false);
    const [bingConnected, setBingConnected] = React.useState(false);
    const [gbpListing, setGbpListing] = React.useState(undefined); // undefined=checking, null=error, obj=result

    // pull live connection status — Google Business Profile is auto-managed
    React.useEffect(() => {
      apiFetch('/api/connections')
        .then((r) => r.json())
        .then((j) => {
          const map = {};
          (j.data || []).forEach((c) => { map[c.provider] = c.status; });
          setGbpConnected(map['google_business'] === 'connected');
          setBingConnected(map['bing_webmaster'] === 'connected');
        })
        .catch(() => {});
    }, []);

    // Detect the REAL Google listing for this NAP (matched by website/phone).
    React.useEffect(() => {
      setGbpListing(undefined);
      const ap = (nap.address || '').split(',');
      const location = ap.length >= 3 ? (ap[1].trim() + ' ' + ap[2].trim().split(/\s+/)[0]) : (ap[1] || '').trim();
      const domain = (nap.website || '').replace(/^https?:\/\//, '').replace(/^www\./i, '').replace(/\/.*$/, '');
      if (!nap.name) { setGbpListing(null); return; }
      window.SGLive.checkGoogleListing(nap.name, location, domain, nap.phone).then((d) => setGbpListing(d || null));
    }, [nap.name, nap.address, nap.website, nap.phone]);

    const setField = (k) => (v) => {
      const next = { ...nap, [k]: v };
      setNap(next);
      try { localStorage.setItem(napKeyFor(domain), JSON.stringify(next)); } catch { /* ignore */ }
    };
    const toggleDone = (id) => {
      const next = { ...done, [id]: !done[id] };
      setDone(next);
      try { localStorage.setItem(doneKeyFor(domain), JSON.stringify(next)); } catch { /* ignore */ }
    };
    const gbpFound = !!(gbpListing && gbpListing.found);
    // Where the Google button should actually send this client.
    //
    // Google resolves business.google.com against whichever profile the signed-in
    // Google account manages -- which is why an ABRE domain opened AI Biz
    // Connect's profile. When we have matched a real listing we link straight to
    // THAT listing instead, so there is nothing to resolve and nothing to
    // mistake. When we have not, the create flow is correct, and is labelled as
    // creating rather than opening.
    const gbpAction = gbpFound
      ? {
          actionUrl: (gbpListing && gbpListing.url) || d0GbpManage,
          actionLabel: 'View your listing',
          accountNote: 'Opens the listing we matched to this website. To edit it you must be signed in to the Google account that manages it — not another one.',
        }
      : {
          actionUrl: 'https://business.google.com/create',
          actionLabel: 'Create your profile',
          accountNote: 'Only do this if this business genuinely has no profile yet. If one already exists, claim it instead — a second profile splits your reviews and rankings.',
        };
    const isListed = (d) => (d.id === 'gbp' ? gbpFound : !!done[d.id]);

    const total = DIRS.length;
    const listedCount = DIRS.filter(isListed).length;
    const actionCount = DIRS.filter((d) => !isListed(d) && (d.importance === 'Critical' || d.importance === 'High')).length;
    const pct = Math.round((listedCount / total) * 100);

    const auto = DIRS.filter((d) => d.group === 'auto');
    const guided = DIRS.filter((d) => d.group === 'guided');

    // ── Real "listings optimization" score ──────────────────────
    // Verified Google profile (45) + other directories listed (35) + business-
    // details completeness (20). Computed from live state, not a fixed number.
    const checking = gbpListing === undefined;
    const napFields = ['name', 'address', 'phone', 'website', 'category'];
    const napComplete = napFields.filter((k) => (nap[k] || '').trim()).length / napFields.length;
    const otherDirs = DIRS.filter((d) => d.id !== 'gbp');
    const otherListed = otherDirs.filter((d) => !!done[d.id]).length;
    const otherScore = otherDirs.length ? otherListed / otherDirs.length : 0;
    const realScore = Math.round((gbpFound ? 45 : 0) + otherScore * 35 + napComplete * 20);
    const scoreColor = realScore >= 70 ? 'var(--sg-green)' : realScore >= 40 ? 'var(--sg-amber)' : 'var(--sg-red)';
    const realSummary = realScore >= 70 ? 'Strong local presence — your business is well-listed and consistent across the web.'
      : realScore >= 40 ? 'A solid start. Adding a few more key directory listings will lift your local visibility.'
      : gbpFound ? 'You’re on Google, but most directories still have no listing — add the key ones below.'
      : 'Barely visible — no verified Google listing yet and most directories are empty. Start with Google.';

    // Real priority fixes derived from actual state.
    const realFixes = [];
    if (!checking && !gbpFound) realFixes.push({ id: 'gbp-create', task: 'Create & verify your Google Business Profile', sev: 'Critical', auto: false, url: 'https://business.google.com/create', detail: 'No verified Google listing matches your website — the single biggest local ranking factor. Create one and verify it.' });
    napFields.forEach((k) => { if (!(nap[k] || '').trim()) realFixes.push({ id: 'nap-' + k, task: 'Add your business ' + k, sev: 'High', auto: false, detail: 'Your ' + k + ' is missing — complete it above so every listing stays consistent.' }); });
    otherDirs.filter((d) => !done[d.id] && (d.importance === 'Critical' || d.importance === 'High')).slice(0, 4).forEach((d) => realFixes.push({ id: 'list-' + d.id, task: 'Get listed on ' + d.name, sev: d.importance, auto: d.method === 'import', url: d.url, detail: d.why }));

    const STAT = [
      { label: 'Listed', value: `${listedCount} / ${total}`, accent: 'var(--sg-green)' },
      { label: 'Action needed', value: String(actionCount), accent: actionCount ? 'var(--sg-amber)' : 'var(--sg-text)' },
      { label: 'Optimization', value: checking ? '…' : realScore + '%', accent: scoreColor },
      // Without a business name we never ran the lookup, so "Not found" would be
      // a finding we did not earn. Say what is actually true: we have not checked.
      { label: 'On Google', value: checking ? '…' : !(nap.name || '').trim() ? 'Add your name' : (gbpFound ? 'Verified' : 'Not found'), accent: gbpFound ? 'var(--sg-green)' : 'var(--sg-amber)' },
    ];

    const SectionCard = ({ title, sub, right, items }) => (
      <Card>
        <CardHead title={title} sub={sub} right={right} />
        {items.map((d) => (
          <DirRow key={d.id} d={d} auto={d.group === 'auto'} listed={isListed(d)}
            {...(d.id === 'gbp' ? gbpAction : {})}
            expanded={expanded === d.id} onToggleExpand={() => setExpanded(expanded === d.id ? null : d.id)}
            onToggleDone={() => toggleDone(d.id)} />
        ))}
      </Card>
    );

    return (
      <div style={{ maxWidth: 1180, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 20 }}>
        <PageHead eyebrow="Local SEO" title="Local Listings" icon="map-pin"
          sub="Get your business onto the directories that feed Google, Apple, Bing and the AI engines — with the same name, address and phone everywhere. Consistency is what local rankings are built on." />

        {/* Real Google Business Profile status (detected & matched) */}
        <GoogleListingCard listing={gbpListing} domain={domain} />

        {/* live listings-optimization score banner (computed from real state) */}
        <Card style={{ padding: 20, display: 'flex', alignItems: 'center', gap: 20 }}>
          <div style={{ position: 'relative', width: 92, height: 92, flex: 'none' }}>
            <svg viewBox="0 0 92 92" width="92" height="92">
              <circle cx="46" cy="46" r="40" fill="none" stroke="var(--sg-sunken)" strokeWidth="9" />
              <circle cx="46" cy="46" r="40" fill="none" stroke={scoreColor} strokeWidth="9" strokeLinecap="round"
                strokeDasharray={2 * Math.PI * 40} strokeDashoffset={2 * Math.PI * 40 * (1 - (checking ? 0 : realScore) / 100)}
                transform="rotate(-90 46 46)" style={{ transition: 'stroke-dashoffset 0.6s var(--sg-ease)' }} />
            </svg>
            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <span className="sg-tnum" style={{ fontFamily: 'var(--sg-font-display)', fontWeight: 700, fontSize: 24, color: scoreColor, lineHeight: 1 }}>{checking ? '…' : realScore + '%'}</span>
            </div>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
              <span style={{ fontFamily: 'var(--sg-font-display)', fontWeight: 600, fontSize: 17, color: 'var(--sg-text)' }}>Listing visibility: {realScore >= 70 ? 'strong' : realScore >= 40 ? 'getting there' : 'needs work'}</span>
              <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.04em', color: 'var(--sg-green)', background: 'var(--sg-green-50)', padding: '2px 8px', borderRadius: 'var(--sg-radius-pill)' }}>● LIVE</span>
            </div>
            <p style={{ margin: '6px 0 0', fontSize: 13, color: 'var(--sg-text-2)', lineHeight: 1.55 }}>{realSummary} Scored from your verified Google profile, the directories you’ve listed on, and how complete your business details are.</p>
          </div>
        </Card>

        {/* priority fixes (tasks) — derived from your real listing state */}
        <PriorityFixes fixes={realFixes} done={done} onToggleDone={toggleDone} />

        {/* source of truth */}
        <Card>
          <CardHead title="Business details — your source of truth" sub="Keep these identical on every directory below. Edit once, copy everywhere."
            right={<span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color: 'var(--sg-text-3)' }}><Icon name="edit" size={14} /> Saved locally</span>} />
          <div style={{ padding: 20, display: 'grid', gridTemplateColumns: '1.3fr 1.6fr 1fr', gap: 14 }}>
            <NapField label="Business name" value={nap.name} onChange={setField('name')} />
            <NapField label="Address" value={nap.address} onChange={setField('address')} />
            <NapField label="Phone" value={nap.phone} onChange={setField('phone')} mono />
            <NapField label="Website" value={nap.website} onChange={setField('website')} mono />
            <NapField label="Primary category" value={nap.category} onChange={setField('category')} />
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <button onClick={() => {
                const text = `${nap.name}\n${nap.address}\n${nap.phone}\n${nap.website}\n${nap.category}`;
                if (navigator.clipboard) navigator.clipboard.writeText(text);
              }}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 7, height: 40, padding: '0 16px', border: '1px solid var(--sg-border)', borderRadius: 'var(--sg-radius-md)', background: 'var(--sg-card)', cursor: 'pointer', fontFamily: 'var(--sg-font-sans)', fontWeight: 700, fontSize: 13, color: 'var(--sg-text-2)' }}>
                <Icon name="download" size={15} /> Copy NAP
              </button>
            </div>
          </div>
        </Card>

        {/* summary */}
        <Card style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', overflow: 'hidden' }}>
          {STAT.map((s, i) => (
            <div key={s.label} style={{ padding: '18px 20px', borderLeft: i ? '1px solid var(--sg-border)' : 'none' }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--sg-text-2)', marginBottom: 8 }}>{s.label}</div>
              <span className="sg-tnum" style={{ fontFamily: 'var(--sg-font-display)', fontWeight: 700, fontSize: 26, letterSpacing: '-0.02em', color: s.accent || 'var(--sg-text)', lineHeight: 1 }}>{s.value}</span>
            </div>
          ))}
        </Card>

        <SectionCard title="Auto-publish — via your connected account" sub="We hold the API connection, so this one we can push for you (no manual entry)."
          right={<span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--sg-green)', background: 'var(--sg-green-50)', padding: '3px 10px', borderRadius: 'var(--sg-radius-pill)' }}>● AUTO</span>}
          items={auto} />

        <SectionCard title="Submission tasks — guided directories"
          sub={`${guided.length} free citation sources, prioritized for Canada. No paid syndication — each is a task you complete with one-click, step-by-step help.`}
          items={guided} />

        {/* directory discovery agent */}
        <Card style={{ padding: 0, overflow: 'hidden', border: '1px solid var(--sg-violet-200, #DDD3F7)' }}>
          <div style={{ padding: 20, display: 'flex', alignItems: 'center', gap: 16, background: 'linear-gradient(135deg, var(--sg-violet-50) 0%, var(--sg-card) 70%)' }}>
            <span style={{ width: 44, height: 44, flex: 'none', borderRadius: 13, background: 'var(--sg-grad-violet)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 14px rgba(124,58,237,0.32)' }}>
              <Icon name="sparkles" size={20} color="#fff" />
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontFamily: 'var(--sg-font-display)', fontWeight: 600, fontSize: 16, color: 'var(--sg-text)' }}>Find more directories for your niche & area</span>
                <span style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '0.06em', color: '#7C3AED', background: 'var(--sg-violet-100)', padding: '2px 7px', borderRadius: 'var(--sg-radius-pill)' }}>AI AGENT</span>
              </div>
              <div style={{ fontSize: 13, color: 'var(--sg-text-2)', marginTop: 3, lineHeight: 1.5 }}>
                Our agent scans for <strong>local & industry directories</strong> specific to your business type and city — free and paid — plus relevant <strong>link-exchange opportunities</strong>, and adds them here as tasks.
              </div>
            </div>
            <button onClick={() => alert('Directory Discovery Agent — coming soon. It will use your business category (' + nap.category + ') and city to surface niche + local directories and link-exchange partners.')}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8, height: 42, padding: '0 18px', flex: 'none', border: 'none', borderRadius: 'var(--sg-radius-pill)', cursor: 'pointer',
                fontFamily: 'var(--sg-font-sans)', fontWeight: 700, fontSize: 13.5, color: '#fff', background: 'var(--sg-grad-violet)', boxShadow: '0 3px 12px rgba(124,58,237,0.3)' }}>
              <Icon name="search" size={16} color="#fff" /> Discover directories
            </button>
          </div>
        </Card>

        {/* GHL Listings (Yext) resold add-on */}
        <Card style={{ padding: 18, display: 'flex', alignItems: 'center', gap: 14, background: 'var(--sg-blue-50)', border: '1px solid var(--sg-blue-100, #DCE6FB)' }}>
          <span style={{ width: 40, height: 40, flex: 'none', borderRadius: 12, background: 'var(--sg-grad-brand)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon name="zap" size={19} color="#fff" />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--sg-text)' }}>Hands-off auto-sync — premium add-on</div>
            <div style={{ fontSize: 13, color: 'var(--sg-text-2)', marginTop: 2, lineHeight: 1.5 }}>
              Want every directory kept in sync automatically (and duplicates suppressed)? Add the managed Listings service — real-time syndication to 50+ directories. AI Biz Connect handles setup; billed monthly.
            </div>
          </div>
          <button style={{ display: 'inline-flex', alignItems: 'center', gap: 7, height: 40, padding: '0 16px', flex: 'none', border: '1px solid var(--sg-blue-600)', borderRadius: 'var(--sg-radius-pill)', background: 'var(--sg-card)', cursor: 'pointer', fontFamily: 'var(--sg-font-sans)', fontWeight: 700, fontSize: 13, color: 'var(--sg-blue-600)' }}>
            Learn more
          </button>
        </Card>
      </div>
    );
  }

  window.SGListings = Listings;
})();
